import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Star, Heart, Ticket, Play, HelpCircle } from 'lucide-react';
import { UserData } from '../hooks/useUser';
import { cn } from '../lib/utils';
import { StarIcon, ShellIcon, CloudIcon, GoldRushIcon } from './ModeIcons';
import { Prism, getBaseTraitValue, getPotentialDescription, getPrismDescription } from '../utils/prismSystem';
import Prism3D from './Prism3D';
import { audioEngine } from '../utils/audioEngine';
import { adManager } from '../utils/adManager';
import CurrencyIcon from './CurrencyIcon';
import { useTranslation } from 'react-i18next';


interface ChooseModeProps {
  user: UserData;
  onBack: () => void;
  onStartGame: (mode: 'pemdas' | 'shellcrush' | 'starburst' | 'goldrush', isPractice?: boolean) => void;
  onShowAlert: (title: string, message: string, type?: any) => void;
  onShowPrismManager: (mode?: 'pemdas' | 'shellcrush' | 'goldrush') => void;
  onRedeemTicket: () => Promise<boolean>;
  onShowShop: () => void;
  onIncrementAdCounter: (type: 'heart' | 'box' | 'star_practice') => Promise<void>;
  onNextTutorialStep?: () => void;
}

const potentialColorMap: Record<string, string> = {
  gc_multi: "#fbbf24",
  gc_bonus: "#fbbf24", // Legacy
  exp_bonus: "#10b981",
  reward_multi: "#f97316",
  hybrid_bonus: "#f97316", // Legacy
  reward_flat: "#6366f1",
  score_bonus: "#a855f7",
  fever_bonus: "#f43f5e",
  time_bonus: "#38bdf8"
};

export default function ChooseMode({
  user,
  onBack,
  onStartGame,
  onShowAlert,
  onShowPrismManager,
  onRedeemTicket,
  onShowShop,
  onIncrementAdCounter,
  onNextTutorialStep
}: ChooseModeProps) {
  const { t } = useTranslation();
  const [selectedModeForPreview, setSelectedModeForPreview] = useState<'pemdas' | 'shellcrush' | 'starburst' | 'goldrush' | null>(null);
  const [showHeartAlert, setShowHeartAlert] = useState(false);
  const [ticketAnimationActive, setTicketAnimationActive] = useState(false);
  const [showHelpCenter, setShowHelpCenter] = useState(false);
  const [helpTab, setHelpTab] = useState<'rules' | 'anima'>('rules');

  const modes = [
    { id: 'starburst', name: t('modes.starburst.name'), sub: t('modes.starburst.sub'), icon: StarIcon, color: 'from-slate-800 to-slate-950', border: 'border-white/10' },
    { id: 'pemdas', name: t('modes.pemdas.name'), sub: t('modes.pemdas.sub'), icon: CloudIcon, color: 'from-[#0ea5e9] to-[#0284c7]', border: 'border-[#0369a1]' },
    { id: 'goldrush', name: t('modes.goldrush.name'), sub: t('modes.goldrush.sub'), icon: GoldRushIcon, color: 'from-[#475569] to-[#1e293b]', border: 'border-slate-400/20', text: 'text-white' },
    { id: 'shellcrush', name: t('modes.shellcrush.name'), sub: t('modes.shellcrush.sub'), icon: ShellIcon, color: 'from-[#fef3c7] to-[#fde68a]', border: 'border-[#d97706]', text: 'text-slate-900' }
  ];

  const getStarPracticeLimitText = () => {
    const counters = user.adCounters;
    if (!counters) return null;
    if (counters.starPracticeAdsCount >= 5) return t('chooseMode.practice_limit');
    return t('chooseMode.practice_ad') + ` (${counters.starPracticeAdsCount}/5)`;
  };

  const handleModeClick = (modeId: string) => {
    audioEngine.playSound('click');
    setSelectedModeForPreview(modeId as any);
  };

  const handleStartGame = (practice: boolean = false) => {
    if (!selectedModeForPreview) return;
    
    if (!practice) {
      if (selectedModeForPreview === 'starburst') {
        if (user.tutorialStep === 33) {
          setTicketAnimationActive(true);
          return;
        }
        if ((user.starTickets || 0) <= 0) {
          onShowAlert(t('chooseMode.no_ticket_alert.title'), t('chooseMode.no_ticket_alert.message'), "warning");
          return;
        }
      } else if (user.hearts <= 0) {
        setShowHeartAlert(true);
        return;
      }
    }
    
    const mode = selectedModeForPreview;
    
    if (practice && mode === 'starburst') {
      if (user.isPremium) {
        setSelectedModeForPreview(null);
        onStartGame(mode, true);
        return;
      }

      // Check Limit
      const counters = user.adCounters;
      if (counters && counters.starPracticeAdsCount >= 5) {
        onShowAlert(t('chooseMode.daily_limit_alert.title'), t('chooseMode.daily_limit_alert.message'), "info");
        return;
      }

      // Show ad before practice
      (async () => {
        const success = await adManager.showRewardedAd('star_practice');
        if (success) {
          await onIncrementAdCounter('star_practice');
          setSelectedModeForPreview(null);
          onStartGame(mode, true);
        } else {
          onShowAlert(t('chooseMode.ad_failed_alert.title'), t('chooseMode.ad_failed_alert.message'), "error");
        }
      })();
      return;
    }

    setSelectedModeForPreview(null);
    onStartGame(mode, practice);
  };

  return (
    <motion.div 
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      transition={{ duration: 0.2 }}
      className="absolute inset-0 z-[999] bg-slate-950/85 backdrop-blur-md flex flex-col p-6 overflow-hidden"
    >
      <div className="flex justify-between items-center mb-6 relative z-10">
        <button onClick={() => setShowHelpCenter(true)} className="flex items-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 rounded-2xl text-cyan-400 hover:text-cyan-300 font-num text-xs font-black uppercase tracking-wider transition-colors border border-cyan-400/20 shadow-[0_0_15px_rgba(34,211,238,0.1)]">
          <HelpCircle className="w-4 h-4" />
          <span>{t('chooseMode.help_center')}</span>
        </button>
        <button onClick={onBack} aria-label="Back to main menu" className="p-3 bg-white/5 rounded-2xl hover:bg-white/10 transition-colors text-slate-400 hover:text-white">
          <X className="w-6 h-6" />
        </button>
      </div>

      <div className="flex flex-col items-center justify-center gap-4 flex-1 overflow-y-auto pb-10 scrollbar-none relative z-10">
        {modes.map((mode) => {
          const completedModes = user.completedStandardModes || [];
          const isTutorialActive = !user.isTutorialCompleted && user.tutorialStep > 0;
          const isStarburstLocked = isTutorialActive && mode.id === 'starburst' && completedModes.length < 3;
          const isModeCompleted = isTutorialActive && mode.id !== 'starburst' && completedModes.includes(mode.id);

          return (
          <button
            key={mode.id}
            id={`mode-${mode.id}`}
            onClick={() => !isStarburstLocked && handleModeClick(mode.id)}
            className={cn(
              "w-full max-w-[280px] bg-gradient-to-br py-4 px-6 rounded-3xl flex items-center justify-center shadow-xl transition-all relative overflow-hidden border-b-[6px] group",
              mode.color,
              mode.border,
              mode.text || "text-white",
              isStarburstLocked || isModeCompleted ? "opacity-50 pointer-events-none grayscale" : "active:scale-95"
            )}
          >
            <div className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity" style={{ backgroundImage: 'linear-gradient(90deg, #fff 1px, transparent 1px)', backgroundSize: '8px 100%' }} />
            <div className="absolute inset-0 bg-white/5" style={{ clipPath: 'polygon(0 0, 100% 0, 50% 50%)' }} />

            {isStarburstLocked ? (
              <div className="absolute top-0 left-0 bg-slate-800 text-slate-300 text-[8px] font-black px-4 py-1.5 rounded-br-2xl shadow-xl border-r border-b border-slate-700 z-20 uppercase tracking-widest">
                {t('chooseMode.completing_unlocks')}
              </div>
            ) : isModeCompleted ? (
              <div className="absolute top-0 left-0 bg-emerald-500 text-white text-[8px] font-black px-4 py-1.5 rounded-br-2xl shadow-xl border-r border-b border-emerald-400 z-20 uppercase tracking-widest">
                {t('chooseMode.completed')}
              </div>
            ) : mode.id === 'starburst' && (
              <motion.div 
                animate={{ 
                  boxShadow: ["0 0 0px rgba(251,191,36,0)", "0 0 20px rgba(251,191,36,0.6)", "0 0 0px rgba(251,191,36,0)"]
                }}
                transition={{ duration: 2, repeat: Infinity }}
                className="absolute top-0 left-0 bg-amber-400 text-slate-900 text-[8px] font-black px-4 py-1.5 rounded-br-2xl shadow-xl border-r border-b border-amber-300 z-20 uppercase tracking-widest"
              >
                {t('chooseMode.special_mode')}
              </motion.div>
            )}

            <div className="flex items-center gap-4 relative z-10 w-full justify-center">
              <mode.icon className="w-12 h-12 shrink-0" />
              <div className="text-left w-32">
                <span className="text-2xl font-num font-black tracking-tight leading-none block">{mode.name}</span>
              </div>
            </div>
          </button>
        )})}
      </div>

      {/* Mode Preview Modal */}
      <AnimatePresence>
        {selectedModeForPreview && (
          <div className="absolute inset-0 z-[1001] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setSelectedModeForPreview(null)}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 40 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 40 }}
              className="game-card-3d border-none w-full max-w-sm overflow-hidden relative z-10 flex flex-col rounded-[48px]"
            >
              {/* Mode Header */}
              <div className={cn(
                "p-8 text-center relative rounded-t-[44px]",
                selectedModeForPreview === 'starburst' ? "bg-slate-900" :
                selectedModeForPreview === 'pemdas' ? "bg-sky-500" : 
                selectedModeForPreview === 'goldrush' ? "bg-slate-800" : "bg-amber-400"
              )}>
                <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '12px 12px' }} />
                <div className="w-20 h-20 bg-white/20 rounded-3xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm shadow-xl">
                  {selectedModeForPreview === 'starburst' ? <StarIcon className="w-12 h-12 text-white" /> :
                   selectedModeForPreview === 'pemdas' ? <CloudIcon className="w-12 h-12 text-white" /> : 
                   selectedModeForPreview === 'goldrush' ? <GoldRushIcon className="w-12 h-12 text-white" /> : 
                   <ShellIcon className="w-12 h-12 text-white" />}
                </div>
                <h4 className="text-3xl font-num text-white uppercase italic tracking-tighter leading-none">
                  {t(`modes.${selectedModeForPreview}.name`)}
                </h4>
                <p className="text-[10px] text-white/60 font-black uppercase tracking-[0.2em] mt-1">{t('chooseMode.high_score')}: {user?.highScores?.[selectedModeForPreview]?.toLocaleString() || 0}</p>
              </div>

              {selectedModeForPreview === 'starburst' && (
                <div className="p-6 bg-slate-50 border-b border-slate-200">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2.5">
                      <span className="px-2.5 py-1 bg-amber-100 text-amber-700 text-[10px] font-black rounded-md uppercase tracking-wider">{t('chooseMode.special_rewards')}</span>
                      <div className="flex items-center gap-1.5 text-xs font-black text-slate-700 uppercase tracking-widest">
                        <span>EXP</span>
                        <span className="opacity-50">•</span>
                        <CurrencyIcon type="gem" size="sm" className="translate-y-[-0.5px]" />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="px-2 py-8 bg-white flex-1">
                {/* Anima Selector Shortcut */}
                <div className="mb-8 px-6">
                  <div className="flex justify-between items-center mb-3">
                    <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{t('prism.active_anima')}</span>
                    {selectedModeForPreview !== 'starburst' && (
                      <button 
                        onClick={() => onShowPrismManager(selectedModeForPreview as any)}
                        className="text-[10px] font-black text-cyan-500 uppercase tracking-widest hover:text-cyan-600 transition-colors"
                      >
                        {t('prism.change')}
                      </button>
                    )}
                  </div>
                  {selectedModeForPreview === 'starburst' ? (
                    <div className="bg-rose-50 p-4 rounded-3xl text-center border border-dashed border-rose-200">
                      <span className="text-[10px] font-black text-rose-500 uppercase">{t('prism.anima_restricted')}</span>
                    </div>
                  ) : (user.equippedPrismIds?.[selectedModeForPreview as 'pemdas' | 'shellcrush' | 'goldrush'] || user.equippedPrismId) ? (() => {
                    const activePrismId = user.equippedPrismIds?.[selectedModeForPreview as 'pemdas' | 'shellcrush' | 'goldrush'] || user.equippedPrismId;
                    const eq = user.prismInventory.find(p => p.id === activePrismId);
                    if (!eq) return null;
                    return (
                      <div className="bg-slate-50 p-5 rounded-3xl flex items-center gap-6 border border-slate-100 shadow-sm border-b-4 border-slate-200 w-full">
                        <div className="flex flex-col items-center gap-1 shrink-0">
                          <div className="w-12 h-12">
                            <Prism3D color={eq.color} stars={eq.stars} isCorrupted={!!eq.blackStats} potentials={[eq.slot1, eq.slot2, eq.slot3].filter(Boolean) as any} />
                          </div>
                          <div className="flex items-center gap-0.5">
                            {[...Array(eq.stars)].map((_, i) => (
                              <Star key={i} className="w-2.5 h-2.5 text-amber-400 fill-amber-400" />
                            ))}
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-col gap-2">
                             {(() => {
                               const desc = getPrismDescription(eq);
                               const [label, value] = desc.split(' +');
                               return (
                                 <div className="flex items-center justify-between gap-2 mt-1">
                                   <div className="flex items-center gap-2">
                                     <div className={cn(
                                       "w-5 h-5 rounded flex items-center justify-center shadow-sm shrink-0",
                                       eq.color === 'red' ? 'bg-rose-500' :
                                       eq.color === 'yellow' ? 'bg-amber-400' :
                                       eq.color === 'green' ? 'bg-emerald-500' :
                                       eq.color === 'blue' ? 'bg-sky-400' : 'bg-violet-500'
                                     )}>
                                       <Star className="w-3 h-3 text-white fill-white drop-shadow-md" />
                                     </div>
                                     <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest truncate">
                                         {label}
                                      </span>
                                   </div>
                                   {value && (
                                     <span className="text-[8px] font-black text-cyan-600">
                                       +{value}
                                     </span>
                                   )}
                                 </div>
                               );
                             })()}
                             
                             {/* Render Potentials */}
                             <div className="flex flex-col gap-1 mt-0.5">
                               {[eq.slot1, eq.slot2, eq.slot3].filter(Boolean).map((pot, idx) => {
                                 if (!pot.type) return null;
                                 const desc = getPotentialDescription(pot);
                                 const [label, value] = desc.split(' +');
                                 return (
                                   <div key={idx} className="flex items-center justify-between gap-2 mt-0.5">
                                     <div className="flex items-center gap-2">
                                       <div className="w-5 h-5 rounded flex items-center justify-center shadow-sm shrink-0" style={{ backgroundColor: potentialColorMap[pot.type] }}>
                                         <span className="text-[10px] font-black text-white leading-none drop-shadow-md">{pot.stars}</span>
                                       </div>
                                       <span className="text-[8px] font-black text-slate-400 uppercase tracking-widest truncate">{label}</span>
                                     </div>
                                     <span className="text-[8px] font-black text-cyan-600">
                                       +{value}
                                     </span>
                                   </div>
                                 )
                               })}
                             </div>
                          </div>
                        </div>
                      </div>
                    );
                  })() : (
                    <div className="bg-slate-50 p-4 rounded-3xl text-center border border-dashed border-slate-200">
                      <span className="text-[10px] font-black text-slate-400 uppercase">{t('prism.no_prism_equipped')}</span>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-1 gap-3 px-6 relative">
                  {/* Floating Ticket Animation */}
                  <AnimatePresence>
                    {ticketAnimationActive && (
                      <motion.div
                        initial={{ opacity: 1, y: 0, scale: 1, x: 0 }}
                        animate={{ opacity: 0, y: -200, scale: 0.6, x: 80 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 1.0, ease: "easeOut" }}
                        className="absolute z-[1002] pointer-events-none"
                        style={{ top: 0, right: 24 }}
                        onAnimationComplete={() => {
                          setTicketAnimationActive(false);
                          onNextTutorialStep?.();
                          onStartGame('starburst', false);
                        }}
                      >
                        <div className="w-16 h-10 rounded-lg bg-gradient-to-br from-amber-400 to-yellow-500 shadow-2xl border-2 border-yellow-300 flex items-center justify-center rotate-12">
                          <Star className="w-5 h-5 text-white fill-white" />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    id="starburst-play-btn"
                    onClick={() => handleStartGame(false)}
                    className={cn(
                      "w-full bg-[#fbbf24] text-slate-900 font-black py-6 rounded-3xl toy-3d-button border-none text-2xl flex items-center justify-center gap-3",
                      !user.isTutorialCompleted && [30, 31, 32].includes(user.tutorialStep) && "pointer-events-none opacity-80"
                    )}
                  >
                    <span>{t('chooseMode.play')}</span>
                    <div className="flex items-center gap-1 bg-black/10 px-3 py-1 rounded-full text-sm">
                      {selectedModeForPreview === 'starburst' ? (
                        <>
                          <Star className="w-4 h-4 fill-amber-500 text-amber-400" />
                          <span>{t('chooseMode.x1_ticket')}</span>
                        </>
                      ) : (
                        <>
                          <Heart className="w-4 h-4 fill-rose-500 text-rose-500" />
                          <span>{t('chooseMode.x1_heart')}</span>
                        </>
                      )}
                    </div>
                  </button>
                               {selectedModeForPreview === 'starburst' && (
                    <button
                      id="starburst-practice-btn"
                      onClick={() => handleStartGame(true)}
                      className={cn(
                        "w-full bg-slate-100 text-slate-600 font-black py-5 rounded-3xl toy-3d-button border-none text-xl flex items-center justify-center gap-3 shadow-md",
                        !user.isTutorialCompleted && [30, 31, 32, 33].includes(user.tutorialStep) && "pointer-events-none opacity-80"
                      )}
                    >
                      <span>{t('chooseMode.practice_mode')}</span>
                      <div className="flex items-center gap-1 bg-slate-200 px-3 py-1 rounded-full text-[10px]">
                        {user.isPremium ? (
                          <span className="text-emerald-600 font-black">{t('chooseMode.practice_free')}</span>
                        ) : (
                          <>
                            <Play className="w-3 h-3 text-slate-500 fill-slate-500" />
                            <span>{getStarPracticeLimitText() || t('chooseMode.practice_ad')}</span>
                          </>
                        )}
                      </div>
                    </button>
                  )}
                  
                  <button
                    onClick={() => setSelectedModeForPreview(null)}
                    className="w-full text-slate-400 font-black text-xs uppercase tracking-widest py-2 hover:text-slate-600 transition-colors"
                  >
                    {t('chooseMode.cancel')}
                  </button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Out of Hearts Alert */}
      <AnimatePresence>
        {showHeartAlert && (
          <div className="absolute inset-0 z-[2001] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowHeartAlert(false)}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="game-card-3d border-none w-full max-w-sm p-8 relative z-10 text-center"
            >
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-rose-100 rounded-full flex items-center justify-center animate-pulse">
                  <Heart className="w-10 h-10 text-rose-500 fill-rose-500" />
                </div>
              </div>
              <h3 className="text-3xl font-num mb-2 tracking-tight text-slate-900 uppercase">{t('chooseMode.hearts_alert.title')}</h3>
              <p className="text-slate-500 mb-8 font-black uppercase tracking-widest text-[10px] px-4 leading-relaxed">
                {user.heartTickets > 0 
                  ? t('chooseMode.hearts_alert.message_with_ticket') 
                  : t('chooseMode.hearts_alert.message_no_ticket')}
              </p>
              
              <div className="grid grid-cols-1 gap-4">
                {user.heartTickets > 0 && (
                  <button
                    onClick={async () => {
                      const success = await onRedeemTicket();
                      if (success) {
                        setShowHeartAlert(false);
                        // We stay on this screen
                      }
                    }}
                    className="w-full bg-[#2dd4bf] text-white font-black py-6 toy-3d-button border-none text-xl flex items-center justify-center gap-3"
                  >
                    <Ticket className="w-6 h-6" />
                    {t('chooseMode.hearts_alert.use_ticket', { count: user.heartTickets })}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowHeartAlert(false);
                    onShowShop();
                  }}
                  className="w-full bg-[#fbbf24] text-slate-900 font-black py-6 toy-3d-button border-none text-xl"
                >
                  {t('chooseMode.hearts_alert.go_to_store')}
                </button>
                <button
                  onClick={() => setShowHeartAlert(false)}
                  className="w-full text-slate-400 font-black text-xs uppercase tracking-widest py-2"
                >
                  {t('chooseMode.hearts_alert.dismiss')}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* ── Help Center Drawer ── */}
      <AnimatePresence>
        {showHelpCenter && (
          <div className="fixed inset-0 z-[2005] flex flex-col justify-end pointer-events-auto">
            <motion.div 
              initial={{ opacity: 0 }} 
              animate={{ opacity: 1 }} 
              exit={{ opacity: 0 }} 
              onClick={() => setShowHelpCenter(false)}
              className="absolute inset-0 bg-slate-950/80 backdrop-blur-md"
            />
            
            {/* Modal Box */}
            <motion.div 
              initial={{ y: '100%' }} 
              animate={{ y: 0 }} 
              exit={{ y: '100%' }} 
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="w-full max-w-md mx-auto bg-slate-900 rounded-t-[40px] border-t border-white/10 flex flex-col p-6 max-h-[85vh] overflow-y-auto pb-10 relative z-10"
              style={{ paddingBottom: 'max(2.5rem, env(safe-area-inset-bottom))' }}
            >
              {/* Header */}
              <div className="flex justify-between items-center mb-6">
                <div>
                  <h4 className="text-xl font-num text-white uppercase tracking-tight">{t('chooseMode.help.title')}</h4>
                  <p className="text-[10px] text-cyan-400 font-black uppercase tracking-widest mt-0.5">{t('chooseMode.help.subtitle')}</p>
                </div>
                <button 
                  onClick={() => setShowHelpCenter(false)} 
                  className="p-2.5 bg-white/5 hover:bg-white/10 rounded-full text-slate-400 hover:text-white transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Tabs */}
              <div className="grid grid-cols-2 gap-2 p-1 bg-slate-950 rounded-2xl mb-6">
                <button 
                  onClick={() => setHelpTab('rules')}
                  className={cn(
                    "py-3 rounded-xl font-num text-[10px] font-black uppercase tracking-wider transition-all",
                    helpTab === 'rules' ? "bg-cyan-400 text-white shadow-lg" : "text-slate-400 hover:text-white"
                  )}
                >
                  {t('chooseMode.help.tab_rules')}
                </button>
                <button 
                  onClick={() => setHelpTab('anima')}
                  className={cn(
                    "py-3 rounded-xl font-num text-[10px] font-black uppercase tracking-wider transition-all",
                    helpTab === 'anima' ? "bg-cyan-400 text-white shadow-lg" : "text-slate-400 hover:text-white"
                  )}
                >
                  {t('chooseMode.help.tab_anima')}
                </button>
              </div>

              {/* Tab Content */}
              <div className="flex-1 overflow-y-auto scrollbar-none space-y-6">
                {helpTab === 'rules' ? (
                  <div className="space-y-4">
                    {/* Visual Card 1: PEMDAS Swipe */}
                    <div className="p-4 bg-white/5 border border-white/5 rounded-2xl flex gap-4 items-start">
                      <div className="p-3 bg-amber-400/10 rounded-xl text-amber-400 font-num font-black text-lg">1</div>
                      <div>
                        <h5 className="text-xs font-black text-white uppercase tracking-wider mb-1">{t('chooseMode.help.rules.rule_1_title')}</h5>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          {t('chooseMode.help.rules.rule_1_desc')}
                        </p>
                      </div>
                    </div>

                    {/* Visual Card 2: Chain Rules */}
                    <div className="p-4 bg-white/5 border border-white/5 rounded-2xl flex gap-4 items-start">
                      <div className="p-3 bg-emerald-400/10 rounded-xl text-emerald-400 font-num font-black text-lg">2</div>
                      <div>
                        <h5 className="text-xs font-black text-white uppercase tracking-wider mb-1">{t('chooseMode.help.rules.rule_2_title')}</h5>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          {t('chooseMode.help.rules.rule_2_desc')}
                        </p>
                      </div>
                    </div>

                    {/* Visual Card 3: Fever Mode */}
                    <div className="p-4 bg-white/5 border border-white/5 rounded-2xl flex gap-4 items-start">
                      <div className="p-3 bg-rose-400/10 rounded-xl text-rose-400 font-num font-black text-lg">3</div>
                      <div>
                        <h5 className="text-xs font-black text-white uppercase tracking-wider mb-1">{t('chooseMode.help.rules.rule_3_title')}</h5>
                        <p className="text-[11px] text-slate-400 leading-relaxed">
                          {t('chooseMode.help.rules.rule_3_desc')}
                        </p>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Anima Info */}
                    <div className="p-4 bg-cyan-400/5 border border-cyan-400/10 rounded-2xl">
                      <h5 className="text-xs font-black text-cyan-400 uppercase tracking-widest mb-1.5">{t('prism.what_title')}</h5>
                      <p className="text-[11px] text-slate-300 leading-relaxed">
                        {t('prism.what_desc')}
                      </p>
                    </div>

                    {/* Stat reference list */}
                    <div className="space-y-3">
                      <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">{t('prism.traits_label')}</div>
                      
                      <div className="grid grid-cols-2 gap-2">
                        <div className="p-3 bg-red-500/10 border border-red-500/10 rounded-xl">
                          <div className="text-xs font-black text-red-400 uppercase">{t('prism.cores.red.name')}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{t('prism.cores.red.desc')}</div>
                        </div>
                        <div className="p-3 bg-amber-500/10 border border-amber-500/10 rounded-xl">
                          <div className="text-xs font-black text-amber-400 uppercase">{t('prism.cores.yellow.name')}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{t('prism.cores.yellow.desc')}</div>
                        </div>
                        <div className="p-3 bg-emerald-500/10 border border-emerald-500/10 rounded-xl">
                          <div className="text-xs font-black text-emerald-400 uppercase">{t('prism.cores.green.name')}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{t('prism.cores.green.desc')}</div>
                        </div>
                        <div className="p-3 bg-blue-500/10 border border-blue-500/10 rounded-xl">
                          <div className="text-xs font-black text-blue-400 uppercase">{t('prism.cores.blue.name')}</div>
                          <div className="text-[10px] text-slate-400 mt-1">{t('prism.cores.blue.desc')}</div>
                        </div>
                      </div>
                      
                      <div className="p-3 bg-purple-500/10 border border-purple-500/10 rounded-xl">
                        <div className="text-xs font-black text-purple-400 uppercase">{t('prism.cores.violet.name')}</div>
                        <div className="text-[10px] text-slate-400 mt-1">{t('prism.cores.violet.desc')}</div>
                      </div>
                    </div>

                    {/* Evolution slot locks description */}
                    <div className="space-y-3">
                      <div className="text-[10px] font-black text-slate-500 uppercase tracking-widest px-1">{t('prism.slots.label')}</div>
                      <div className="p-4 bg-white/5 border border-white/5 rounded-2xl space-y-3.5">
                        <div className="flex justify-between items-center text-xs">
                          <span className="font-bold text-slate-300">{t('prism.slots.slot_1star')}</span>
                          <span className="text-[10px] font-black text-cyan-400 uppercase bg-cyan-400/10 px-2 py-0.5 rounded-full">{t('prism.slots.slot_1star_open')}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs border-t border-white/5 pt-2.5">
                          <span className="font-bold text-slate-300">{t('prism.slots.slot_2star')}</span>
                          <span className="text-[10px] font-black text-cyan-400 uppercase bg-cyan-400/10 px-2 py-0.5 rounded-full">{t('prism.slots.slot_2star_open')}</span>
                        </div>
                        <div className="flex justify-between items-center text-xs border-t border-white/5 pt-2.5">
                          <span className="font-bold text-slate-300">{t('prism.slots.slot_3star')}</span>
                          <span className="text-[10px] font-black text-emerald-400 uppercase bg-emerald-400/10 px-2 py-0.5 rounded-full">{t('prism.slots.slot_3star_open')}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
